import { useEffect } from "react";
import { YStack, Text, ScrollView } from "tamagui";
import { colors } from "@styles/colors";
import { Header, Fade } from "./Common";
import { useForm, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { combinedAddress } from "@components/address/func/addressTool";
import { useAddressStore } from "@utils/store/utils/useAddressStore";
import AddressAutoComplete from "@components/address/AddressAutoComplete";
import AddressForm from "@components/address/AddressForm";
import { cleanKeyword } from "@components/address/func/addressTextTool";
import { getAddressSelectionCoordinates, normalizeAddressSelectionData } from "@components/address/func/addressSelectionTool";
import RadiusContentWrapper from "@components/layout/RadiusContentWrapper";

const formSchema = z.object({
  addressId: z.number().optional().nullable(),
  city: z.string().min(1, "請輸入城市"),
  area: z.string().min(1, "請輸入區域"),
  road: z.string().min(1, "請輸入路名"),
  streetNumber: z.string().min(1, "請輸入門牌號"),
  floor: z.string().optional(),
  room: z.string().optional(),
  otherAddress: z.string().optional(),
  latitude: z.number().optional().nullable(),
  longitude: z.number().optional().nullable(),
});

type FormData = z.infer<typeof formSchema>;

export default function StepAddress({ onBack, onNext, addr, setAddr }: any) {
  const { setAutoCompleteAddress, autoCompleteAddress, addressViewMode, setAddressViewMode, setMapPickerDisabled } = useAddressStore();

  const methods = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      addressId: null,
      city: "",
      area: "",
      road: "",
      streetNumber: "",
      floor: "",
      room: "",
      otherAddress: "",
      latitude: null,
      longitude: null,
    },
  });

  //-- 當點擊搜尋結果時，同步到表單
  useEffect(() => {
    if (autoCompleteAddress) {
      const normalizedAddress = normalizeAddressSelectionData(autoCompleteAddress);
      const coords = getAddressSelectionCoordinates(normalizedAddress);
      methods.setValue("city", normalizedAddress?.administrative_area_level_1 || "");
      methods.setValue("area", normalizedAddress?.administrative_area_level_2 || "");
      methods.setValue("road", normalizedAddress?.route || "");
      methods.setValue("streetNumber", cleanKeyword(normalizedAddress?.street_number || "", "號"));
      methods.setValue("latitude", coords?.lat ?? null);
      methods.setValue("longitude", coords?.lng ?? null);
    }
  }, [autoCompleteAddress]);

  //-- 初始化模式
  useEffect(() => {
    setAddressViewMode("autoComplete");
    setAutoCompleteAddress(null);
    setMapPickerDisabled(false);
  }, []);

  const onSubmit = (data: FormData) => {
    setAddr(combinedAddress(data));
    onNext();
  };

  return (
    <RadiusContentWrapper padding="0" space={0} background="transparent">
      <Header title="瓦斯要送到哪？" sub="步驟 1 / 3　填好地址再幫你找附近的瓦斯行" step={1} total={3} onBack={onBack} />
      <ScrollView showsVerticalScrollIndicator={false}>
        <YStack p="$0" gap="$0">
          <Fade i={0}>
            {addressViewMode === "autoComplete" ? (
              <AddressAutoComplete />
            ) : (
              <YStack>
                <AddressForm methods={methods} onSubmit={onSubmit} />
              </YStack>
            )}
          </Fade>
        </YStack>
      </ScrollView>
    </RadiusContentWrapper>
  );
}
